const YOUTUBE_ID = /^[a-zA-Z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com']);
const PATH_ID_PREFIXES = ['/embed/', '/shorts/', '/live/'];

const getYouTubeVideoId = (url: URL): string | null => {
  const host = url.hostname.toLowerCase();
  if (host === 'youtu.be') return url.pathname.slice(1).split('/')[0];
  if (!YOUTUBE_HOSTS.has(host)) return null;
  if (url.pathname === '/watch') return url.searchParams.get('v');
  const prefix = PATH_ID_PREFIXES.find((p) => url.pathname.startsWith(p));
  return prefix ? url.pathname.slice(prefix.length).split('/')[0] : null;
};

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

/**
 * Converts a YouTube watch, share, embed, Shorts or live URL into an autoplaying embed URL.
 * On `/watch` links the video ID is read from the `v` query parameter wherever it appears in the
 * query string, so playlist and timestamped watch links work too.
 * @param url - Any video URL
 * @returns The embed URL, or `null` when the URL is not a recognized YouTube link
 */
export function getYouTubeEmbedUrl(url: string): string | null {
  // Authors sometimes paste links without the scheme (www.youtube.com/watch?v=...).
  const parsed = parseUrl(url) ?? parseUrl(`https://${url}`);
  if (!parsed) return null;
  const id = getYouTubeVideoId(parsed);
  if (!id || !YOUTUBE_ID.test(id)) return null;
  // Keep the privacy-enhanced host when the author chose it, so the embed doesn't set tracking cookies.
  const embedHost = parsed.hostname.toLowerCase().endsWith('youtube-nocookie.com')
    ? 'www.youtube-nocookie.com'
    : 'www.youtube.com';
  return `https://${embedHost}/embed/${id}?autoplay=1`;
}
